import { useRef } from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { RiCloseLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { Button, type ButtonProps } from './button';
import { type IconLike } from './icon';
import { IconChip } from './icon-chip';
import { type ChipColor } from './lib/colors';
import { usePortalContainer, type PortalContainer } from './lib/portal';

export interface ModalProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the modal (optional — modals are usually controlled). */
  trigger?: React.ReactElement;
  /**
   * `form` — modal A (565:23921 "Grafikni saqlash"): w480, r12, header h56 (chip 32 + 600 15/21 + bare close 20).
   * `create` — modal B (429:16550 / 317:7966 "Yangi bo'lim"): w433, r18, header chip 44 + 700 18 title + 500 13
   * subtitle + close 32 r9 White/5.
   */
  variant?: 'form' | 'create';
  /** Width in design px (default 480 / 433). */
  width?: number;
  /** Header chip. */
  icon?: { icon: IconLike; color?: ChipColor };
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  /** Footer actions, right-aligned (Secondary + Primary). */
  footer?: React.ReactNode;
  /** Hide the × button. */
  hideClose?: boolean;
  closeLabel?: string;
  /** Keep the modal open on outside clicks. */
  disablePointerDismissal?: boolean;
  /** Element to focus on open (default: the first field of the body). */
  initialFocus?: React.RefObject<HTMLElement | null>;
  /** Element to focus after closing (default: the element that was focused before opening). */
  finalFocus?: React.RefObject<HTMLElement | null>;
  className?: string;
  bodyClassName?: string;
  /** Portal target (scaled stage). Defaults to the nearest `PortalContainerProvider`, else `<body>`. */
  container?: PortalContainer;
}

/** Scrim + centring viewport shared by Modal and ConfirmDialog. */
export const overlayBackdrop =
  'fixed inset-0 z-60 bg-overlay transition-opacity duration-200 ease-standard data-starting-style:opacity-0 data-ending-style:opacity-0';
export const overlayViewport = 'fixed inset-0 z-70 flex items-center justify-center overflow-y-auto p-16';
export const overlayPopupMotion =
  'transition-[opacity,scale] duration-200 ease-standard data-starting-style:scale-96 data-starting-style:opacity-0 data-ending-style:scale-96 data-ending-style:opacity-0';

/** Modal dialog — Base UI Dialog with the two product chromes of spec §6.9. */
export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  variant = 'form',
  width,
  icon,
  title,
  subtitle,
  children,
  footer,
  hideClose,
  closeLabel = 'Yopish',
  disablePointerDismissal,
  initialFocus,
  finalFocus,
  className,
  bodyClassName,
  container,
}: ModalProps) {
  const portalContainer = usePortalContainer(container);
  const bodyRef = useRef<HTMLDivElement>(null);
  const form = variant === 'form';
  const w = width ?? (form ? 480 : 433);
  return (
    <BaseDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
      disablePointerDismissal={disablePointerDismissal}
    >
      {trigger && <BaseDialog.Trigger render={trigger} />}
      <BaseDialog.Portal container={portalContainer}>
        <BaseDialog.Backdrop className={overlayBackdrop} />
        <BaseDialog.Viewport className={overlayViewport}>
          <BaseDialog.Popup
            finalFocus={finalFocus}
            // focus the first field of the body (not the header × button) unless told otherwise
            initialFocus={
              initialFocus ??
              (() =>
                bodyRef.current?.querySelector<HTMLElement>(
                  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ) ?? true)
            }
            style={{ width: `${w / 16}rem` }}
            className={cx(
              'flex max-h-full max-w-full flex-col overflow-hidden border border-line bg-modal text-fg outline-none',
              form ? 'rounded-12' : 'rounded-18',
              overlayPopupMotion,
              className,
            )}
          >
            {form ? (
              <header className="flex h-56 shrink-0 items-center gap-10 px-16">
                {icon && <IconChip icon={icon.icon} color={icon.color ?? 'darkBlue'} size={32} />}
                <div className="flex min-w-0 flex-1 flex-col">
                  <BaseDialog.Title className="truncate t-card-title text-fg">{title}</BaseDialog.Title>
                  {subtitle && <BaseDialog.Description className="truncate text-xs text-fg-secondary">{subtitle}</BaseDialog.Description>}
                </div>
                {!hideClose && (
                  <BaseDialog.Close
                    aria-label={closeLabel}
                    className="flex size-24 shrink-0 cursor-pointer items-center justify-center rounded-4 text-icon transition-colors hover:bg-hover hover:text-fg"
                  >
                    <RiCloseLine aria-hidden className="size-20" />
                  </BaseDialog.Close>
                )}
              </header>
            ) : (
              <header className="flex shrink-0 items-center gap-14 pt-18 pr-20 pb-14 pl-24">
                {icon && <IconChip icon={icon.icon} color={icon.color ?? 'orange'} size={44} />}
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <BaseDialog.Title className="text-lg leading-auto font-bold tracking-normal text-fg">{title}</BaseDialog.Title>
                  {subtitle && (
                    <BaseDialog.Description className="text-13 leading-auto font-medium text-fg-secondary">{subtitle}</BaseDialog.Description>
                  )}
                </div>
                {!hideClose && (
                  <BaseDialog.Close
                    aria-label={closeLabel}
                    className="flex size-32 shrink-0 cursor-pointer items-center justify-center rounded-9 bg-subtle text-icon transition-colors hover:bg-field hover:text-fg"
                  >
                    <RiCloseLine aria-hidden className="size-16" />
                  </BaseDialog.Close>
                )}
              </header>
            )}
            <div aria-hidden className="h-px shrink-0 bg-line" />
            <div
              ref={bodyRef}
              className={cn('flex min-h-0 flex-col overflow-y-auto', form ? 'gap-16 p-16' : 'gap-14 px-24 pt-14 pb-16', bodyClassName)}
            >
              {children}
            </div>
            {footer && (
              <>
                <div aria-hidden className="h-px shrink-0 bg-line" />
                <footer
                  className={cn(
                    'flex shrink-0 items-center justify-end',
                    form ? 'min-h-56 gap-8 px-16 py-12' : 'gap-10 px-24 pt-16 pb-20',
                  )}
                >
                  {footer}
                </footer>
              </>
            )}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/** A footer button that closes the modal ("Bekor qilish"). Secondary by default. */
export function ModalClose({ variant = 'secondary', ...props }: ButtonProps) {
  return <BaseDialog.Close render={<Button variant={variant} {...props} />} />;
}
